import apiClient, { setAuthTokens, clearAuthTokens } from './apiClient';
import Logger from '../utils/logger';
import { validateAndSanitizeData } from '../utils/validationUtils';

// User-related API functions
export const userService = {
  // Get all users
  getUsers: async () => {
    try {
      const response = await apiClient.get('/admin/users');
      return response;
    } catch (error) {
      Logger.error('Failed to fetch users', {
        error: error.message,
        status: error.response?.status,
        url: '/admin/users'
      });
      throw error;
    }
  },

  // Get user by ID
  getUserById: async (id) => {
    try {
      const response = await apiClient.get(`/admin/users/${id}`);
      return response;
    } catch (error) {
      Logger.error(`Failed to fetch user with ID ${id}`, {
        error: error.message,
        status: error.response?.status,
        url: `/admin/users/${id}`
      });
      throw error;
    }
  },

  // Create a new user
  createUser: async (userData) => {
    // Validate and sanitize input
    const validation = validateAndSanitizeData(userData);
    if (!validation.isValid) {
      throw new Error(`Invalid input: ${validation.errors.join(', ')}`);
    }

    try {
      const response = await apiClient.post('/admin/users', validation.data);
      Logger.info('User created successfully', {
        userId: response.data.id,
        username: validation.data.username
      });
      return response;
    } catch (error) {
      Logger.error('Failed to create user', {
        error: error.message,
        status: error.response?.status,
        userData: validation.data.username ? { username: validation.data.username } : validation.data
      });
      throw error;
    }
  },

  // Update user
  updateUser: async (id, userData) => {
    // Validate and sanitize input
    const validation = validateAndSanitizeData(userData);
    if (!validation.isValid) {
      throw new Error(`Invalid input: ${validation.errors.join(', ')}`);
    }

    try {
      const response = await apiClient.put(`/admin/users/${id}`, validation.data);
      Logger.info('User updated successfully', {
        userId: id,
        updatedFields: Object.keys(validation.data)
      });
      return response;
    } catch (error) {
      Logger.error(`Failed to update user with ID ${id}`, {
        error: error.message,
        status: error.response?.status,
        url: `/admin/users/${id}`,
        userData: validation.data
      });
      throw error;
    }
  },

  // Delete user
  deleteUser: async (id) => {
    try {
      await apiClient.delete(`/admin/users/${id}`);
      Logger.info('User deleted successfully', {
        userId: id
      });
    } catch (error) {
      Logger.error(`Failed to delete user with ID ${id}`, {
        error: error.message,
        status: error.response?.status,
        url: `/admin/users/${id}`
      });
      throw error;
    }
  },

  // Reset user password
  resetPassword: async (id, newPassword) => {
    // Validate and sanitize input
    const validation = validateAndSanitizeData({ newPassword });
    if (!validation.isValid) {
      throw new Error(`Invalid input: ${validation.errors.join(', ')}`);
    }

    try {
      const response = await apiClient.post(`/admin/users/${id}/reset-password`, validation.data);
      Logger.info('User password reset successfully', {
        userId: id
      });
      return response;
    } catch (error) {
      Logger.error(`Failed to reset password for user with ID ${id}`, {
        error: error.message,
        status: error.response?.status,
        url: `/admin/users/${id}/reset-password`
      });
      throw error;
    }
  },
};

// Auth-related API functions
export const authService = {
  // Login
  login: async (credentials) => {
    // Validate and sanitize input
    const validation = validateAndSanitizeData(credentials);
    if (!validation.isValid) {
      throw new Error(`Invalid input: ${validation.errors.join(', ')}`);
    }

    try {
      const response = await apiClient.post('/auth/login', validation.data);

      // Store access and refresh tokens in memory if they exist in the response
      if (response.data.accessToken) {
        setAuthTokens(response.data.accessToken, response.data.refreshToken);
      }

      Logger.info('Login successful', {
        username: validation.data.username,
        userId: response.data.userProfile?.id
      });
      return response;
    } catch (error) {
      Logger.error('Login failed', {
        error: error.message,
        status: error.response?.status,
        username: validation.data.username
      });
      throw error;
    }
  },

  // Logout
  logout: () => {
    // Clear tokens from memory
    clearAuthTokens();
    Logger.info('User logged out');
  },

  // Register
  register: async (userData) => {
    try {
      const response = await apiClient.post('/auth/register', userData);
      Logger.info('User registered successfully', {
        userId: response.data.id,
        username: userData.username
      });
      return response;
    } catch (error) {
      Logger.error('Registration failed', {
        error: error.message,
        status: error.response?.status,
        userData: { username: userData.username }
      });
      throw error;
    }
  },

  // Get current user profile
  getProfile: async () => {
    try {
      const response = await apiClient.get('/auth/profile');
      return response;
    } catch (error) {
      Logger.error('Failed to fetch user profile', {
        error: error.message,
        status: error.response?.status,
        url: '/auth/profile'
      });
      throw error;
    }
  },

  // Change Password
  changePassword: async (currentPassword, newPassword) => {
    // Validate and sanitize input
    const validation = validateAndSanitizeData({ currentPassword, newPassword });
    if (!validation.isValid) {
      throw new Error(`Invalid input: ${validation.errors.join(', ')}`);
    }

    try {
      const response = await apiClient.post('/auth/change-password', validation.data);
      Logger.info('Password changed successfully');
      return response;
    } catch (error) {
      Logger.error('Failed to change password', {
        error: error.message,
        status: error.response?.status,
        url: '/auth/change-password'
      });
      throw error;
    }
  },
};

// Role-related API functions
export const roleService = {
  // Get all roles
  getRoles: async () => {
    try {
      const response = await apiClient.get('/admin/roles');
      return response;
    } catch (error) {
      Logger.error('Failed to fetch roles', {
        error: error.message,
        status: error.response?.status,
        url: '/admin/roles'
      });
      throw error;
    }
  },

  // Get role by ID
  getRoleById: async (id) => {
    try {
      const response = await apiClient.get(`/admin/roles/${id}`);
      return response;
    } catch (error) {
      Logger.error(`Failed to fetch role with ID ${id}`, {
        error: error.message,
        status: error.response?.status,
        url: `/admin/roles/${id}`
      });
      throw error;
    }
  },

  // Create a new role
  createRole: async (roleData) => {
    try {
      const response = await apiClient.post('/admin/roles', roleData);
      Logger.info('Role created successfully', {
        roleId: response.data.id,
        roleName: roleData.name
      });
      return response;
    } catch (error) {
      Logger.error('Failed to create role', {
        error: error.message,
        status: error.response?.status,
        roleData: { name: roleData.name }
      });
      throw error;
    }
  },

  // Update role
  updateRole: async (id, roleData) => {
    try {
      const response = await apiClient.put(`/admin/roles/${id}`, roleData);
      Logger.info('Role updated successfully', {
        roleId: id,
        updatedFields: Object.keys(roleData)
      });
      return response;
    } catch (error) {
      Logger.error(`Failed to update role with ID ${id}`, {
        error: error.message,
        status: error.response?.status,
        url: `/admin/roles/${id}`,
        roleData
      });
      throw error;
    }
  },

  // Delete role
  deleteRole: async (id) => {
    try {
      await apiClient.delete(`/admin/roles/${id}`);
      Logger.info('Role deleted successfully', {
        roleId: id
      });
    } catch (error) {
      Logger.error(`Failed to delete role with ID ${id}`, {
        error: error.message,
        status: error.response?.status,
        url: `/admin/roles/${id}`
      });
      throw error;
    }
  },
};

// Permission-related API functions
export const permissionService = {
  // Get all permissions
  getPermissions: async () => {
    try {
      const response = await apiClient.get('/admin/permissions');
      return response;
    } catch (error) {
      Logger.error('Failed to fetch permissions', {
        error: error.message,
        status: error.response?.status,
        url: '/admin/permissions'
      });
      throw error;
    }
  },

  // Get permission by ID
  getPermissionById: async (id) => {
    try {
      const response = await apiClient.get(`/admin/permissions/${id}`);
      return response;
    } catch (error) {
      Logger.error(`Failed to fetch permission with ID ${id}`, {
        error: error.message,
        status: error.response?.status,
        url: `/admin/permissions/${id}`
      });
      throw error;
    }
  },

  // Create a new permission
  createPermission: async (permissionData) => {
    try {
      const response = await apiClient.post('/admin/permissions', permissionData);
      Logger.info('Permission created successfully', {
        permissionId: response.data.id,
        permissionName: permissionData.name
      });
      return response;
    } catch (error) {
      Logger.error('Failed to create permission', {
        error: error.message,
        status: error.response?.status,
        permissionData: { name: permissionData.name }
      });
      throw error;
    }
  },

  // Update permission
  updatePermission: async (id, permissionData) => {
    try {
      const response = await apiClient.put(`/admin/permissions/${id}`, permissionData);
      Logger.info('Permission updated successfully', {
        permissionId: id,
        updatedFields: Object.keys(permissionData)
      });
      return response;
    } catch (error) {
      Logger.error(`Failed to update permission with ID ${id}`, {
        error: error.message,
        status: error.response?.status,
        url: `/admin/permissions/${id}`,
        permissionData
      });
      throw error;
    }
  },

  // Delete permission
  deletePermission: async (id) => {
    try {
      await apiClient.delete(`/admin/permissions/${id}`);
      Logger.info('Permission deleted successfully', {
        permissionId: id
      });
    } catch (error) {
      Logger.error(`Failed to delete permission with ID ${id}`, {
        error: error.message,
        status: error.response?.status,
        url: `/admin/permissions/${id}`
      });
      throw error;
    }
  },
};

// Settings-related API functions
export const settingsService = {
  // Get all settings
  getSettings: async () => {
    try {
      const response = await apiClient.get('/admin/settings');
      return response;
    } catch (error) {
      Logger.error('Failed to fetch settings', {
        error: error.message,
        status: error.response?.status,
        url: '/admin/settings'
      });
      throw error;
    }
  },

  // Get setting by ID
  getSettingById: async (id) => {
    try {
      const response = await apiClient.get(`/admin/settings/${id}`);
      return response;
    } catch (error) {
      Logger.error(`Failed to fetch setting with ID ${id}`, {
        error: error.message,
        status: error.response?.status,
        url: `/admin/settings/${id}`
      });
      throw error;
    }
  },

  // Update setting
  updateSetting: async (id, settingData) => {
    try {
      const response = await apiClient.put(`/admin/settings/${id}`, settingData);
      Logger.info('Setting updated successfully', {
        settingId: id,
        updatedFields: Object.keys(settingData)
      });
      return response;
    } catch (error) {
      Logger.error(`Failed to update setting with ID ${id}`, {
        error: error.message,
        status: error.response?.status,
        url: `/admin/settings/${id}`,
        settingData
      });
      throw error;
    }
  },
};

// Report-related API functions
export const reportService = {
  /**
   * Fetches a paginated list of reports.
   * @param {object} params - The query parameters for the request.
   * @param {number} params.page - The page number to fetch.
   * @param {number} params.size - The number of items per page.
   * @returns {Promise<object>} The response from the API containing the list of reports.
   */
  getReports: (params) => {
    return apiClient.get('/admin/reports', { params });
  },

  /**
   * Fetches a report by ID.
   * @param {number} id - The ID of the report to fetch.
   * @returns {Promise<object>} The response from the API containing the report details.
   */
  getReportById: (id) => {
    return apiClient.get(`/admin/reports/${id}`);
  },

  /**
   * Generates a report.
   * @param {object} reportData - The data for generating the report.
   * @returns {Promise<object>} The response from the API.
   */
  generateReport: (reportData) => {
    return apiClient.post('/admin/reports/generate', reportData);
  },

  /**
   * Downloads a report.
   * @param {number} id - The ID of the report to download.
   * @returns {Promise<object>} The response from the API.
   */
  downloadReport: (id) => {
    return apiClient.get(`/admin/reports/${id}/download`);
  }
};

// Warehouse-related API functions (for FundedHere integration)
export const warehouseService = {
  /**
   * Fetches all warehouses from the FundedHere API.
   * @returns {Promise<object>} The response from the API containing the list of warehouses.
   */
  getWarehouses: () => {
    // This would typically call the FundedHere API through a dedicated service
    // For now, using the main API client - in a real implementation this would call the external API
    return apiClient.get('/warehouses');
  },

  /**
   * Creates a new warehouse in the FundedHere API.
   * @param {object} warehouseData - The data for the new warehouse.
   * @returns {Promise<object>} The response from the API.
   */
  createWarehouse: (warehouseData) => {
    return apiClient.post('/warehouses', warehouseData);
  },

  /**
   * Updates an existing warehouse.
   * @param {number} id - The ID of the warehouse to update.
   * @param {object} warehouseData - The updated data for the warehouse.
   * @returns {Promise<object>} The response from the API.
   */
  updateWarehouse: (id, warehouseData) => {
    return apiClient.put(`/warehouses/${id}`, warehouseData);
  },

  /**
   * Deletes a warehouse.
   * @param {number} id - The ID of the warehouse to delete.
   * @returns {Promise<object>} The response from the API.
   */
  deleteWarehouse: (id) => {
    return apiClient.delete(`/warehouses/${id}`);
  }
};

// Partner Product-related API functions (for FundedHere integration)
export const partnerProductService = {
  /**
   * Fetches a paginated list of partner products.
   * @param {object} params - The query parameters for the request.
   * @returns {Promise<object>} The response from the API containing the list of partner products.
   */
  getPartnerProducts: (params) => {
    return apiClient.get('/partner-products', { params });
  },

  /**
   * Fetches partner products by client ID.
   * @param {string} clientId - The client ID to filter by.
   * @param {object} params - The query parameters for the request.
   * @returns {Promise<object>} The response from the API containing the list of partner products.
   */
  getPartnerProductsByClientId: (clientId, params = {}) => {
    return apiClient.get(`/partner-products/client/${clientId}`, { params });
  },

  /**
   * Creates a new partner product.
   * @param {object} productData - The data for the new partner product.
   * @returns {Promise<object>} The response from the API.
   */
  createPartnerProduct: (productData) => {
    return apiClient.post('/partner-products', productData);
  },

  /**
   * Updates an existing partner product.
   * @param {number} id - The ID of the partner product to update.
   * @param {object} productData - The updated data for the partner product.
   * @returns {Promise<object>} The response from the API.
   */
  updatePartnerProduct: (id, productData) => {
    return apiClient.put(`/partner-products/${id}`, productData);
  },

  /**
   * Toggles the status of a partner product.
   * @param {number} id - The ID of the partner product.
   * @param {number} status - The new status.
   * @returns {Promise<object>} The response from the API.
   */
  togglePartnerProductStatus: (id, status) => {
    return apiClient.put(`/partner-products/${id}/status`, { status });
  },

  /**
   * Uploads a file of partner products.
   * @param {FormData} fileData - The file data to upload.
   * @returns {Promise<object>} The response from the API.
   */
  uploadPartnerProductsFile: (fileData) => {
    return apiClient.post('/products/upload', fileData, {
      headers: {
        'Content-Type': undefined,
      },
      onUploadProgress: (progressEvent) => {
        Math.round((progressEvent.loaded * 100) / progressEvent.total);
      }
    });
  },

  /**
   * Registers a partner product with the FundedHere WMS.
   * @param {number} id - The ID of the partner product.
   * @param {object} productData - The product data for registration.
   * @returns {Promise<object>} The response from the API.
   */
  registerPartnerProduct: (id, productData) => {
    return apiClient.put(`/partner-products/${id}`, {
      clientId: productData.clientId,
      warehouseId: productData.warehouseId,
      productId: productData.productId,
      productName: productData.productName,
      unitPrice: productData.unitPrice,
      status: productData.status || 1,
      registered: 1
    });
  }
};

// Service-related API functions (from servicesService.js)
export const serviceManagementService = {
  /**
   * Fetches a paginated list of services.
   * @param {object} params - The query parameters for the request.
   * @param {number} params.page - The page number to fetch.
   * @param {number} params.size - The number of items per page.
   * @returns {Promise<object>} The response from the API containing the list of services.
   */
  getServices: (params) => {
    return apiClient.get('/admin/services', { params });
  },

  /**
   * Fetches a service by ID.
   * @param {number} id - The ID of the service to fetch.
   * @returns {Promise<object>} The response from the API containing the service details.
   */
  getServiceById: (id) => {
    return apiClient.get(`/admin/services/${id}`);
  },

  /**
   * Updates an existing service.
   * @param {number} id - The ID of the service to update.
   * @param {object} serviceData - The updated data for the service.
   * @returns {Promise<object>} The response from the API.
   */
  updateService: (id, serviceData) => {
    return apiClient.put(`/admin/services/${id}`, serviceData);
  },

  /**
   * Creates a new service.
   * @param {object} serviceData - The data for the new service.
   * @param {string} serviceData.serviceId - The service's ID.
   * @param {string} serviceData.serviceDescription - The service's description.
   * @param {string} serviceData.auth - The auth value.
   * @param {number} [serviceData.status=1] - The status of the service. Defaults to 1.
   * @returns {Promise<object>} The response from the API.
   */
  createService: ({ serviceId, serviceDescription, auth, status = 1 }) => {
    const payload = {
      serviceId,
      serviceDescription,
      auth,
      status,
    };
    return apiClient.post('/admin/services', payload);
  }
};